import {WizardService} from '../../../../../shared/components/wizard/wizard.service';

import {Observable} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {catchError, map} from 'rxjs/operators';
import {OfficeModuleFunction, ProjectInformationStepData} from '../project-wizard.types';
import {
  CreateUpdateProjectRequest,
  OfficeModuleFunctionsRequest,
  ProjectControllerService,
  ProjectDetailResponse,
  ProjectNameCheckResponse,
} from '@frontend/shared-api';

@Injectable({
  providedIn: 'root',
})
export class ProjectWizardService extends WizardService<CreateUpdateProjectRequest> {
  private readonly projectControllerService = inject(ProjectControllerService);
  private readonly GENERAL_INFORMATION_STEP = 1;
  private readonly OFFICE_STEP = 3;

  constructor() {
    super();
  }

  checkProjectNameExists(name: string): Observable<ProjectNameCheckResponse> {
    if (!name?.trim()) {
      throw new Error('Project name is required');
    }

    return this.projectControllerService.checkProjectNameExists({name}).pipe(
      catchError((error) => {
        console.error('Error checking project name: ', error);
        throw error;
      }),
    );
  }

  protected buildPartialDTO(): void {
    try {
      const dto: Partial<CreateUpdateProjectRequest> = {};
      this.processGeneralInformationStep(dto);
      this.processOfficeStep(dto);
      this.updateFormData(dto);
    } catch (error) {
      console.error('Error building partial DTO:', error);
      throw error;
    }
  }

  private processGeneralInformationStep(dto: Partial<CreateUpdateProjectRequest>) {
    const generalInformationData = this.getStepData<ProjectInformationStepData>(
      this.GENERAL_INFORMATION_STEP,
    );
    dto.name = generalInformationData?.name;
    dto.description = generalInformationData?.description;
    dto.yearsInScope = generalInformationData?.yearsInScope;
  }

  private processOfficeStep(dto: Partial<CreateUpdateProjectRequest>) {
    const officeData = this.getStepData<OfficeModuleFunction[]>(this.OFFICE_STEP);

    dto.offices = new Set<OfficeModuleFunction>();

    if (officeData) {
      for (const officeModule of officeData) {
        const officeModuleRequest: OfficeModuleFunctionsRequest = {
          officeId: officeModule.officeId,
          moduleFunctions: new Set(officeModule.moduleFunctions),
        };
        dto.offices.add(officeModuleRequest);
      }
    }
  }

  createProject(request: CreateUpdateProjectRequest): Observable<any> {
    const serializableRequest = this.serializeRequest(request);

    return this.projectControllerService
      .createProject({createUpdateProjectRequest: serializableRequest as any})
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error creating project: ', error);
          throw error;
        }),
      );
  }

  private serializeRequest(request: CreateUpdateProjectRequest) {
    return {
      ...request,
      offices: Array.from(request.offices).map((office) => ({
        officeId: office.officeId,
        moduleFunctions: Array.from(office.moduleFunctions),
      })),
    };
  }

  override buildFinalRequest(): CreateUpdateProjectRequest {
    const data = this.formData;
    return {
      name: data.name || '',
      description: data.description || '',
      yearsInScope: data.yearsInScope ?? 5,
      offices: data.offices ?? new Set<OfficeModuleFunctionsRequest>(),
    };
  }

  getProjectData(id: number): Observable<ProjectDetailResponse> {
    if (!id) {
      throw new Error('Project id is required');
    }

    return this.projectControllerService.getProjectDetails({id}).pipe(
      catchError((error) => {
        console.error('Error fetching project data:', error);
        throw error;
      }),
    );
  }

  updateProject(
    id: number,
    request: CreateUpdateProjectRequest,
  ): Observable<ProjectDetailResponse> {
    const payload = this.serializeRequest(request);
    return this.projectControllerService.updateProject({
      id,
      createUpdateProjectRequest: payload as any,
    });
  }
}
